import { useState, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import type { DraftInput, SaveDraftResponse, CreateTaxDocumentResponse } from "../types";

interface UseDraftReturn {
  saveDraft: (input: DraftInput, draftId?: string) => Promise<SaveDraftResponse>;
  finalizeDraft: (draftId: string) => Promise<CreateTaxDocumentResponse>;
  deleteDraft: (draftId: string) => Promise<void>;
  isSaving: boolean;
  isFinalizing: boolean;
}

export function useDraft(): UseDraftReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const saveDraft = useCallback(
    async (input: DraftInput, draftId?: string): Promise<SaveDraftResponse> => {
      setIsSaving(true);
      try {
        if (draftId) {
          return await apiFetch.patch<SaveDraftResponse>(
            `/api/tax-documents/drafts/${draftId}`,
            input
          );
        } else {
          return await apiFetch.post<SaveDraftResponse>(
            "/api/tax-documents/drafts",
            input
          );
        }
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const finalizeDraft = useCallback(
    async (draftId: string): Promise<CreateTaxDocumentResponse> => {
      setIsFinalizing(true);
      try {
        return await apiFetch.post<CreateTaxDocumentResponse>(
          `/api/tax-documents/drafts/${draftId}/finalize`
        );
      } finally {
        setIsFinalizing(false);
      }
    },
    []
  );

  const deleteDraft = useCallback(async (draftId: string): Promise<void> => {
    await apiFetch.delete(`/api/tax-documents/drafts/${draftId}`);
  }, []);

  return {
    saveDraft,
    finalizeDraft,
    deleteDraft,
    isSaving,
    isFinalizing,
  };
}
